import { Box, Flex, Text } from "@chakra-ui/react";
import { Col, Modal, Row } from "antd";
import { CloseModalButton } from "components/Button/CloseModalButton";
import FooterModal from "components/Button/FooterModal";
import DateRangePicker from "components/Input/DateRangePicker";
import SelectContaBancaria from "components/Input/SelectContaBancaria";
import Toolbar from "components/Toolbar";
import {
  RelatorioInadimplentesQueryParams,
  SetorContaInadimplencia,
} from "core/features/relatorios/typings/index";
import { useState, useEffect } from "react";
import InputMask from "ui/components/Input/InputMask";
import SelectBaseAntd from "ui/components/Input/SelectBaseAntd";
import { defaultFiltroRelatorioInadimplentes } from "./../../hooks/useFiltroRelatorioInadimplentes";
import moment from "moment";
import SelectParametrosJuros from "components/Input/SelectParametrosJuros";
import { Input } from "@chakra-ui/input";
import { tagSetorJuridicoString } from "features/conta/typings";
import SelectContaCategoria from "ui/components/Input/SelectContaCategoria";
import SelectCentroDeCusto from "../../../../../../../components/Input/SelectCentroDeCusto/index";

interface ModalFiltrosRelatorioInadimplentesProps {
  onClose: () => void;
  onReset: () => void;
  filtro: RelatorioInadimplentesQueryParams;
  visible: boolean;
  onChangeFiltro: (filtro: Partial<RelatorioInadimplentesQueryParams>) => void;
}

export default function ModalFiltrosRelatorioInadimplentes({
  onClose,
  visible,
  onChangeFiltro,
  onReset,
  filtro,
}: ModalFiltrosRelatorioInadimplentesProps) {
  const [localFiltro, setLocalFiltro] =
    useState<Partial<RelatorioInadimplentesQueryParams>>(filtro);

  const numeroFiltroSelecionados =
    (localFiltro.contasBancariasIds?.length ? 1 : 0) +
    (localFiltro.cpf ? 1 : 0) +
    (localFiltro.dataInicio || localFiltro.dataFim ? 1 : 0) +
    (localFiltro.setor ? 1 : 0) +
    (localFiltro.porcentagemHonorarios ? 1 : 0);

  useEffect(() => {
    if (!visible) {
      setLocalFiltro(filtro);
    }
  }, [visible, filtro]);

  return (
    <Modal
      centered
      destroyOnClose
      closable={false}
      width="700px"
      visible={visible}
      className="antd-custom-header"
      title={
        <Toolbar title="Filtros avançados" onClose={onClose} border="0px" />
      }
      footer={
        <Flex
          justifyContent="end"
          align="center"
          gap="8px"
          alignContent="center"
          alignItems="center"
        >
          <CloseModalButton
            closeModalLabel="Fechar"
            close={onClose}
            mt="32px"
          />
          <FooterModal
            isDisabled={false}
            onSubmit={() => {
              onChangeFiltro(localFiltro);
              onClose();
            }}
            onClose={() => {
              onReset();
              setLocalFiltro(defaultFiltroRelatorioInadimplentes);
              onClose();
            }}
            showRegisterAnother={false}
            primaryButtonText={`Filtrar ${
              typeof numeroFiltroSelecionados === "number" &&
              numeroFiltroSelecionados > 0
                ? ` (${numeroFiltroSelecionados})`
                : ""
            }`}
            buttonCancelarText="Limpar filtros"
            isLoadingSave={false}
          />
        </Flex>
      }
    >
      <Box as={Row} gutter={16} mb={4}>
        <Col md={12}>
          <Text mb="2px" fontSize="13px">
            Período de vencimento
          </Text>
          <DateRangePicker
            onChange={(dataInicio, dataFim) => {
              setLocalFiltro((state) => ({ ...state, dataInicio, dataFim }));
            }}
            maxDate={new Date()}
            value={{
              dataInicio: localFiltro.dataInicio,
              dataFim: localFiltro.dataFim,
            }}
            extraStaticRanges={[
              {
                label: "30 dias vencido",
                range: () => ({
                  startDate: moment()
                    .startOf("day")
                    .subtract(30, "days")
                    .toDate(),
                  endDate: moment().startOf("day").toDate(),
                }),
              },
              {
                label: "90 dias vencido",
                range: () => ({
                  startDate: moment()
                    .startOf("day")
                    .subtract(90, "days")
                    .toDate(),
                  endDate: moment().startOf("day").toDate(),
                }),
              },
              {
                label: "+ de 90 dias vencido",
                range: () => ({
                  startDate: moment("2022-01-01").toDate(),
                  endDate: moment()
                    .subtract(90, "days")
                    .startOf("day")
                    .toDate(),
                }),
              },
            ]}
          />
        </Col>

        <Col md={12}>
          <Text mb="2px" fontSize="13px">
            Posição em
          </Text>

          <Input
            type="date"
            onChange={(ev) => {
              setLocalFiltro((state) => ({
                ...state,
                dataPosicao: ev.target.value,
              }));
            }}
            value={localFiltro.dataPosicao}
          />
        </Col>
      </Box>

      <Box as={Row} gutter={16} mb={4}>
        <Col md={12}>
          <Text mb="2px" fontSize="13px">
            Setor de cobrança
          </Text>
          <SelectBaseAntd
            value={localFiltro?.setor}
            onChange={(setor) =>
              setLocalFiltro((state) => ({ ...state, setor }))
            }
            options={[
              {
                label: "Administrativo",
                value: SetorContaInadimplencia.Administrativo,
              },
              {
                label: tagSetorJuridicoString,
                value: SetorContaInadimplencia.Juridico,
              },
            ]}
          />
        </Col>
      </Box>

      <Box as={Row} gutter={16} mb={4}>
        <Box as={Col} xs={12}>
          <Text mb="2px" fontSize="13px">
            Conta bancária
          </Text>
          <SelectContaBancaria
            mode="multiple"
            onChange={(contasBancariasIds) => {
              setLocalFiltro((state) => ({ ...state, contasBancariasIds }));
            }}
            value={localFiltro.contasBancariasIds}
            placeholder="Todas as contas bancárias"
          />
        </Box>
        <Box as={Col} xs={12}>
          <Text mb="2px" fontSize="13px">
            Contas categorias
          </Text>
          <SelectContaCategoria
            multiple
            onChange={(contasCategoriasIds) => {
              setLocalFiltro((state) => ({ ...state, contasCategoriasIds }));
            }}
            value={localFiltro.contasCategoriasIds}
            placeholder="Todas as contas categorias"
          />
        </Box>
      </Box>

      <Box as={Row} gutter={16} mb={4}>
        <Col md={12}>
          <Text mb="2px" fontSize="13px">
            CPF
          </Text>
          <InputMask
            value={localFiltro.cpf}
            onChange={(cpf) => setLocalFiltro((state) => ({ ...state, cpf }))}
            mask="999.999.999-99"
            placeholder="XXX.XXX.XXX-XX"
          />
        </Col>

        <Col md={12}>
          <Text mb="2px" fontSize="13px">
            % honorários
          </Text>
          <SelectParametrosJuros
            value={localFiltro?.porcentagemHonorarios}
            onChange={(porcentagemHonorarios) =>
              setLocalFiltro((state) => ({ ...state, porcentagemHonorarios }))
            }
          />
        </Col>
      </Box>

      <Box as={Row} gutter={16} mb={4}>
        <Box as={Col} md={12} mb={4}>
          <Text mb="2px" fontSize="13px">
            Centro de custo
          </Text>
          <SelectCentroDeCusto
            allowClear
            placeholder="Todos os centros de custo"
            value={localFiltro.centrosDeCustoIds}
            mode="multiple"
            onChange={(centrosDeCustoIds) =>
              setLocalFiltro((state) => ({ ...state, centrosDeCustoIds }))
            }
          />
        </Box>
      </Box>
    </Modal>
  );
}
